import { Alert, Box, Button, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useHierarchyContext } from "../contexts/HierarchyContext";
import {
  listHealthChecksForService,
  type HealthCheck,
  deleteHealthCheckForService,
} from "../api/client";
import { Panel } from "../components/layout/Panel";
import { CompGrid, type Column } from "../components/grid/CompGrid";

export function PageHealthChecks() {
  const { hierarchy } = useHierarchyContext();
  const serviceSlug = hierarchy?.service?.slug ?? null;
  const serviceHealthCheckNewHref = serviceSlug
    ? `/services/${encodeURIComponent(serviceSlug)}/health-checks/new`
    : null;

  const [healthChecks, setHealthChecks] = useState<HealthCheck[]>([]);
  const [healthChecksLoading, setHealthChecksLoading] = useState(false);
  const [healthChecksError, setHealthChecksError] = useState<string | null>(null);

  useEffect(() => {
    if (!serviceSlug) return;
    const abort = new AbortController();
    setHealthChecksLoading(true);
    setHealthChecksError(null);
    listHealthChecksForService(serviceSlug, abort.signal)
      .then((res) => {
        if (abort.signal.aborted) return;
        setHealthChecks(res);
      })
      .catch((err) => {
        if (abort.signal.aborted) return;
        setHealthChecksError(err instanceof Error ? err.message : "Failed to load health checks");
      })
      .finally(() => {
        if (abort.signal.aborted) return;
        setHealthChecksLoading(false);
      });
    return () => abort.abort();
  }, [serviceSlug]);

  const handleDeleteHealthCheck = async (healthCheckId: string) => {
    if (!serviceSlug) return;
    try {
      setHealthChecksLoading(true);
      setHealthChecksError(null);
      await deleteHealthCheckForService(serviceSlug, healthCheckId, undefined);
      setHealthChecks((prev) => prev.filter((hc) => hc.id !== healthCheckId));
    } catch (err) {
      setHealthChecksError(err instanceof Error ? err.message : "Failed to delete health check");
    } finally {
      setHealthChecksLoading(false);
    }
  };

  const healthCheckColumns: Column<HealthCheck>[] = [
    {
      id: "name",
      label: "Name",
      sortable: true,
      sortValue: (hc) => hc.name,
      render: (hc) => (
        <Typography
          variant="body2"
          component={RouterLink}
          to={`/services/${encodeURIComponent(serviceSlug!)}/health-checks/${encodeURIComponent(hc.id)}`}
          sx={{ color: "primary.main", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
        >
          {hc.name}
        </Typography>
      ),
    },
    {
      id: "url",
      label: "URL",
      sortable: true,
      sortValue: (hc) => hc.url,
      render: (hc) => (
        <Typography variant="body2" className="om-text-mono" sx={{ fontSize: "0.875rem" }}>
          {hc.url}
        </Typography>
      ),
    },
    {
      id: "probe_type",
      label: "Type",
      sortable: true,
      sortValue: (hc) => hc.probe_type,
      render: (hc) => <Typography variant="body2">{hc.probe_type}</Typography>,
    },
    {
      id: "probe_interval_ms",
      label: "Interval",
      sortable: true,
      sortValue: (hc) => hc.probe_interval_ms,
      render: (hc) => (
        <Typography variant="body2">{Math.round(hc.probe_interval_ms / 1000)}s</Typography>
      ),
    },
    {
      id: "enabled",
      label: "Status",
      sortable: true,
      sortValue: (hc) => (hc.enabled ? 1 : 0),
      render: (hc) => (
        <Typography variant="body2" sx={{ opacity: 0.75 }}>
          {hc.enabled ? "enabled" : "disabled"}
        </Typography>
      ),
    },
    {
      id: "actions",
      label: serviceHealthCheckNewHref ? (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mx: 4 }}>
          <Button
            size="small"
            variant="text"
            component={RouterLink}
            to={serviceHealthCheckNewHref}
            disabled={healthChecksLoading}
          >
            Create
          </Button>
        </Box>
      ) : null,
      render: (hc) => (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mx: 4 }}>
          <Button
            size="small"
            variant="text"
            onClick={() => handleDeleteHealthCheck(hc.id)}
            disabled={healthChecksLoading}
          >
            Delete
          </Button>
        </Box>
      ),
    },
  ];

  if (!serviceSlug) {
    return (
      <Box>
        <Alert severity="warning">
          No Service in URL. Please navigate from a product's services list.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3, width: "100%" }}>
      <Panel title="Health Checks">
        <CompGrid
          items={healthChecks}
          loading={healthChecksLoading}
          error={healthChecksError}
          emptyMessage="No health checks configured."
          columns={healthCheckColumns}
          keyExtractor={(hc) => hc.id}
          isItemDimmed={(hc) => !hc.enabled}
        />
      </Panel>
    </Box>
  );
}
